/**
 * `entities/approval-chain` API surface.
 *
 * Exposes:
 *   - Service functions for READING approval chain definitions.
 *   - `*Query` queryOptions helpers for TanStack consumers (AD-20 — reads MUST
 *     flow through queryOptions to enforce the cache-key contract).
 *
 * Mutations (delete, duplicate, copy-from) live in `features/manage-approval-chains`
 * per AD-23 (entities expose queries; features own mutations).
 */
import { WorkflowDefinitionsService } from '@inference.engine/workspace-api-client/web';
import { queryOptions } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

import { APPROVAL_CHAIN_QUERY_KEYS } from './query-keys';
import type {
  ApprovalChain,
  ApprovalChainsListQuery,
  ApprovalChainsResponse,
  ApprovalStepRole,
} from '../model/types';

// ──────────────────────────── Service functions ────────────────────────────

export const listApprovalChains = async (
  projectId: string,
  params?: ApprovalChainsListQuery,
): Promise<ApprovalChainsResponse> => {
  const response = await WorkflowDefinitionsService.list({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    query: params,
  });

  if (!response.data) {
    throw new Error('Failed to list approval chain definitions');
  }

  return response.data;
};

export const getApprovalChain = async (
  projectId: string,
  approvalChainId: string,
): Promise<ApprovalChain> => {
  const response = await WorkflowDefinitionsService.getById({
    client: workspaceHttp,
    path: { platformProjectId: projectId, workflowId: approvalChainId },
  });

  if (!response.data) {
    throw new Error('Failed to get approval chain definition');
  }

  return response.data;
};

export const listStepRoles = async (projectId: string): Promise<ApprovalStepRole[]> => {
  const response = await WorkflowDefinitionsService.listStepRoles({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    // A caller who cannot manage members is refused the list, and the step editor says why in
    // place, so that one refusal stays out of the toast; anything unexpected still raises one.
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: '403' },
  });

  if (!response.data) {
    throw new Error('Failed to list approval step roles');
  }

  return response.data.data;
};

/** The server caps `pageSize` at 100, so asking for that many needs the fewest requests. */
const APPROVAL_CHAINS_PAGE_SIZE = 100;

/**
 * Every approval chain in the project. The server pages the list, and a select or tag that
 * read only the first page would treat a live chain on a later one as missing.
 */
export const listAllApprovalChains = async (projectId: string): Promise<ApprovalChainsResponse> => {
  const firstPage = await listApprovalChains(projectId, {
    page: 1,
    pageSize: APPROVAL_CHAINS_PAGE_SIZE,
  });
  if (firstPage.items.length >= firstPage.total) return firstPage;

  // The server reports the page size it applied, which is what the offsets follow.
  const pageCount = Math.ceil(firstPage.total / firstPage.pageSize);
  const laterPageNumbers = Array.from({ length: pageCount - 1 }, (_, index) => index + 2);
  const laterPages = await Promise.all(
    laterPageNumbers.map((page) =>
      listApprovalChains(projectId, {
        page,
        pageSize: firstPage.pageSize,
      }),
    ),
  );

  return {
    ...firstPage,
    items: withoutRepeatedChains([...firstPage.items, ...laterPages.flatMap((page) => page.items)]),
  };
};

/**
 * The pages are offset windows read at different moments, so a chain created between two
 * requests shifts the next window and its last-seen neighbour comes back a second time. An edit
 * or delete in that gap can instead skip a chain; the next refetch of the list recovers it.
 */
function withoutRepeatedChains(chains: ApprovalChain[]): ApprovalChain[] {
  const seenIds = new Set<string>();
  return chains.filter((chain) => {
    if (seenIds.has(chain.id)) return false;
    seenIds.add(chain.id);
    return true;
  });
}

// ──────────────────────────── queryOptions helpers ─────────────────────────

/** Every approval chain under a project, however many pages the server splits them into. */
export const approvalChainsQuery = (projectId: string | undefined) =>
  queryOptions({
    queryKey: APPROVAL_CHAIN_QUERY_KEYS.list(projectId!),
    queryFn: () => listAllApprovalChains(projectId!),
    enabled: !!projectId,
  });

export const stepRolesQuery = (projectId: string | undefined) =>
  queryOptions({
    queryKey: APPROVAL_CHAIN_QUERY_KEYS.stepRoles(projectId!),
    queryFn: () => listStepRoles(projectId!),
    enabled: !!projectId,
    // The refusal is about the caller, not the network; asking again changes nothing.
    retry: false,
  });

export const approvalChainQuery = (
  projectId: string | undefined,
  approvalChainId: string | undefined,
) =>
  queryOptions({
    queryKey: APPROVAL_CHAIN_QUERY_KEYS.detail(projectId!, approvalChainId!),
    queryFn: () => getApprovalChain(projectId!, approvalChainId!),
    enabled: !!projectId && !!approvalChainId,
  });
